import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useSnapshot } from 'valtio';
import { ServiceType } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  InlineRow,
  LoadingBlock,
  Muted,
  PageContainer,
  Stack,
  StatCard,
  StatGrid,
  Strong,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../components/ui';
import {
  useServiceWorkerQuery,
  useWorkerAvailabilityQuery,
  useWorkerQuoteQuery,
} from '../../services/serviceService/__generated__/queries.generated';
import { authStore } from '../../../store/authStore';
import { modalActions } from '../../../store/authStore/modalStore';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import {
  CLEANING_LEVELS,
  CLEANING_LEVEL_LABELS,
  formatMoney,
  SERVICE_TYPE_LABELS,
  VEHICLE_CLASSES,
  VEHICLE_CLASS_LABELS,
} from '../ServiceProviderPage/priceTables';
import { SUPPLY_LABELS } from '../ServiceProviderPage/workerForm';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import {
  QuoteBreakdown,
  WorkerBadges,
  WorkersMap,
  type PublicWorker,
} from './ServiceGuestComponents';
import { ContactWorkerModal } from './ContactWorkerModal';
import { WorkerReviewsCard } from './WorkerReviewsCard';
import { jobItems, onTimeLabel, serviceAreaText, UNINSURED_NOTE } from './serviceJob';
import { formatHomeArea, SERVICE_AREA_LIMITS } from '../../utils/serviceUnits';
import { asAreaUnit } from '../../utils/units';
import {
  bookPath,
  parseServiceSearch,
  quotePlace,
  serviceSearchParams,
  windowProblem,
  windowSummary,
} from './serviceSearchState';

const Columns = styled.div`
  ${tw`grid grid-cols-1 gap-8 lg:grid-cols-3`}
`;

const Main = styled.div`
  ${tw`flex flex-col gap-6 lg:col-span-2 min-w-0`}
`;

const Side = styled.aside`
  ${tw`flex flex-col gap-4 min-w-0`}
  @media (min-width: 1024px) {
    position: sticky;
    top: 96px;
    align-self: start;
  }
`;

const Photos = styled.div`
  ${tw`grid grid-cols-2 gap-2 sm:grid-cols-4`}
  img {
    ${tw`object-cover w-full`}
    aspect-ratio: 4 / 3;
    border-radius: var(--radius-md);
    background: var(--color-bg-secondary);
  }
`;

const Title = styled.h1`
  ${tw`text-2xl font-bold sm:text-3xl`}
  color: var(--color-text-primary);
  font-family: var(--font-display);
`;

const DayRow = styled.div`
  ${tw`flex justify-between gap-4 py-2 text-sm`}
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-primary);
`;

type Offering = PublicWorker['offerings'][number];

function PriceTable({ offering }: { offering: Offering }) {
  const table = (offering.priceTable ?? {}) as Record<string, any>;
  if (offering.serviceType === ServiceType.VehicleHandover) {
    return (
      <Muted $size="sm">
        {formatMoney(offering.hourlyRate ?? 0)} an hour, at least{' '}
        {Math.round(offering.minimumBillableMinutes / 60) || 1} hour. Billed for the time it takes.
      </Muted>
    );
  }
  if (offering.serviceType === ServiceType.VehicleCleaning) {
    return (
      <TableWrap>
        <Table>
          <THead>
            <TR>
              <TH>Vehicle</TH>
              {CLEANING_LEVELS.map(level => (
                <TH key={level}>{CLEANING_LEVEL_LABELS[level]}</TH>
              ))}
            </TR>
          </THead>
          <TBody>
            {VEHICLE_CLASSES.map(vehicleClass => (
              <TR key={vehicleClass}>
                <TD>{VEHICLE_CLASS_LABELS[vehicleClass]}</TD>
                {CLEANING_LEVELS.map(level => {
                  const price = table[vehicleClass]?.[level];
                  return (
                    <TD key={level}>
                      {price == null ? 'Not offered' : formatMoney(Number(price))}
                    </TD>
                  );
                })}
              </TR>
            ))}
          </TBody>
        </Table>
      </TableWrap>
    );
  }
  return (
    <Stack $gap={4}>
      <Muted $size="sm">
        {formatMoney(Number(table.basePrice ?? 0))} for 1 bedroom and 1 bathroom, plus{' '}
        {formatMoney(Number(table.perExtraBedroom ?? 0))} per extra bedroom and{' '}
        {formatMoney(Number(table.perExtraBathroom ?? 0))} per extra bathroom.
        {table.largeHomeSurcharge
          ? ` Homes over ${formatHomeArea(table.largeHomeSqft ?? SERVICE_AREA_LIMITS[asAreaUnit(table.largeHomeAreaUnit)].defaultLarge, table.largeHomeAreaUnit)} add ${formatMoney(Number(table.largeHomeSurcharge))}.`
          : ''}
        {table.sameDayTurnoverSurcharge
          ? ` Same-day turnovers add ${formatMoney(Number(table.sameDayTurnoverSurcharge))}.`
          : ''}
      </Muted>
      {Array.isArray(table.overrides) && table.overrides.length > 0 && (
        <Muted $size="sm">
          Set prices:{' '}
          {table.overrides
            .map(
              (o: any) =>
                `${o.bedrooms === 0 ? 'studio' : `${o.bedrooms} bd`} / ${o.bathrooms} ba ${formatMoney(Number(o.price))}`,
            )
            .join(', ')}
        </Muted>
      )}
    </Stack>
  );
}

/** Busy times of the next days, grouped by day in the worker's zone. */
function Availability({ carId, from, zone }: { carId: string; from: string; zone: string }) {
  const to = DateTime.fromISO(from).plus({ days: 6 }).toISODate()!;
  const query = useWorkerAvailabilityQuery({
    variables: { workerCarId: carId, from, to },
    fetchPolicy: 'cache-and-network',
  });
  if (query.loading && !query.data) return <LoadingBlock label="Loading availability" />;
  if (query.error) return <Muted $size="sm">{stayErrorMessage(query.error)}</Muted>;
  const intervals = query.data?.workerAvailability ?? [];
  const days = Array.from({ length: 7 }, (_, i) =>
    DateTime.fromISO(from, { zone }).plus({ days: i }),
  );
  return (
    <div data-testid="worker-availability">
      {days.map(day => {
        const start = day.startOf('day').toMillis();
        const end = day.endOf('day').toMillis();
        const busy = intervals.filter(b => b.startAt < end && b.endAt > start);
        const closed = busy.some(b => b.kind === 'closed' && b.startAt <= start && b.endAt >= end);
        return (
          <DayRow key={day.toISODate()}>
            <span>{day.toFormat('ccc, LLL d')}</span>
            <Muted $size="sm">
              {closed
                ? 'Closed'
                : busy.filter(b => b.kind !== 'closed').length === 0
                  ? 'Open'
                  : busy
                      .filter(b => b.kind !== 'closed')
                      .map(
                        b =>
                          `${b.kind === 'job' ? 'Booked' : 'Blocked'} ${DateTime.fromMillis(b.startAt, { zone }).toFormat('h:mm a')} to ${DateTime.fromMillis(b.endAt, { zone }).toFormat('h:mm a')}`,
                      )
                      .join(', ')}
            </Muted>
          </DayRow>
        );
      })}
    </div>
  );
}

/**
 * A worker's public page (spec 019 W12): photos and equipment, Provider and
 * area (city and ZIP only), prices, supplies, badges, on-time and cancel
 * rates, the area map, availability and a price for this search. Public
 * (D21): signed-out visitors price by ZIP code.
 */
export default function WorkerDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const state = useMemo(() => parseServiceSearch(params), [params]);
  const [zipDraft, setZipDraft] = useState(state.zip ?? '');
  const { isAuthenticated } = useSnapshot(authStore);
  const [contactOpen, setContactOpen] = useState(false);
  const contactWorker = () => {
    if (!isAuthenticated) {
      modalActions.openSignInModal(() => setContactOpen(true));
      return;
    }
    setContactOpen(true);
  };
  const query = useServiceWorkerQuery({ variables: { carId: id }, skip: !id });
  const car = query.data?.serviceWorker ?? null;
  const worker = car?.serviceWorker ?? null;

  const place = quotePlace(state);
  const items = worker
    ? jobItems(worker.offerings, {
        serviceTypes: [state.serviceType],
        homeSize: state.siteId
          ? null
          : { bedrooms: state.bedrooms, bathrooms: state.bathrooms, sqft: state.sqft },
        sameDayTurnover: state.sameDayTurnover,
        vehicleClass: state.siteId ? null : state.vehicleClass,
        cleaningLevel: state.cleaningLevel,
        handoverHours: state.handoverHours,
      })
    : [];
  const quote = useWorkerQuoteQuery({
    variables: {
      workerCarId: id,
      input: {
        ...(place ?? {}),
        serviceDate: state.date,
        windowStart: state.windowStart,
        windowEnd: state.windowEnd,
        items,
      },
    },
    skip: !worker || !place || items.length === 0 || Boolean(windowProblem(state)),
    fetchPolicy: 'cache-and-network',
  });

  if (query.loading && !car) return <LoadingBlock label="Loading the worker" />;
  if (!car || !worker) {
    return (
      <PageContainer width="md">
        <Alert tone="error" title="Worker not found">
          This worker is not taking jobs. <Link to="/">Search again</Link>
        </Alert>
      </PageContainer>
    );
  }

  const offered = worker.offerings.filter(o => o.isActive);
  const zone = car.location?.timezone || 'America/Phoenix';
  const photos = (car.images ?? []).map(i => i.imageUrl).filter(Boolean) as string[];
  const supplies = (Object.keys(SUPPLY_LABELS) as (keyof typeof SUPPLY_LABELS)[]).filter(
    key => worker.suppliesProvided[key],
  );
  const offersSearched = offered.some(o => o.serviceType === state.serviceType);

  return (
    <PageContainer width="xl" data-testid="worker-detail">
      <Stack $gap={6}>
        <div>
          <Title>{worker.displayName}</Title>
          <Muted>
            {worker.provider.businessName} · {worker.city}, {worker.state} {worker.zip}
          </Muted>
        </div>
        <WorkerBadges worker={worker} />
        <Columns>
          <Main>
            {photos.length > 0 && (
              <Photos>
                {photos.slice(0, 8).map((photo, i) => (
                  <img
                    key={photo}
                    src={toPublicS3ObjectUrl(photo)}
                    alt={`${worker.displayName} ${i + 1}`}
                  />
                ))}
              </Photos>
            )}
            {worker.bio && (
              <Card>
                <CardHeader title={`About ${worker.displayName}`} />
                <CardBody>
                  <Stack $gap={4}>
                    <Muted>{worker.bio}</Muted>
                    <Muted $size="sm">
                      {[
                        worker.yearsExperience != null
                          ? `${worker.yearsExperience} years of experience`
                          : null,
                        worker.languages.length ? `Speaks ${worker.languages.join(', ')}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Muted>
                  </Stack>
                </CardBody>
              </Card>
            )}
            <Card>
              <CardHeader
                title="Services and prices"
                description="Prices before travel, add-ons, our service fee and tax."
              />
              <CardBody>
                <Stack $gap={6}>
                  {offered.map(offering => (
                    <Stack key={offering.id} $gap={4}>
                      <Strong>{SERVICE_TYPE_LABELS[offering.serviceType]}</Strong>
                      <PriceTable offering={offering} />
                      {offering.addOns.length > 0 && (
                        <Muted $size="sm">
                          Add-ons:{' '}
                          {offering.addOns
                            .map(
                              a =>
                                `${a.label} ${formatMoney(a.price)}${a.unit === 'per_item' ? ' each' : ''}`,
                            )
                            .join(', ')}
                        </Muted>
                      )}
                    </Stack>
                  ))}
                </Stack>
              </CardBody>
            </Card>
            {worker.equipmentPhotos.length > 0 && (
              <Card>
                <CardHeader title="Equipment" description="What this worker brings." />
                <CardBody>
                  <Photos>
                    {worker.equipmentPhotos.map((photo, i) => (
                      <img
                        key={photo}
                        src={toPublicS3ObjectUrl(photo)}
                        alt={`Equipment ${i + 1}`}
                      />
                    ))}
                  </Photos>
                </CardBody>
              </Card>
            )}
            <Card>
              <CardHeader title="Supplies" />
              <CardBody>
                <Muted $size="sm">
                  {supplies.length
                    ? `Brings: ${supplies.map(key => SUPPLY_LABELS[key]).join(', ')}.`
                    : 'Brings no supplies. Leave yours where the worker can find them.'}
                </Muted>
              </CardBody>
            </Card>
            <StatGrid $cols={4}>
              <StatCard label="On time" value={onTimeLabel(worker.onTimeRate) ?? 'Too new'} />
              <StatCard
                label="Cancelled"
                value={
                  worker.cancellationRate == null
                    ? 'Too new'
                    : `${Math.round(worker.cancellationRate * 100)}%`
                }
              />
              <StatCard label="Jobs done" value={worker.completedJobCount} />
              <StatCard
                label="Rating"
                value={worker.averageRating ? worker.averageRating.toFixed(1) : 'None yet'}
                hint={worker.totalRatings ? `${worker.totalRatings} reviews` : undefined}
              />
            </StatGrid>
            <Card>
              <CardHeader title="Service area" description={serviceAreaText(worker)} />
              <CardBody>
                {worker.areaCenter ? (
                  <WorkersMap
                    height={320}
                    center={{ lat: worker.areaCenter.lat, lng: worker.areaCenter.lng }}
                    workers={[
                      {
                        id: worker.carId,
                        lat: worker.areaCenter.lat,
                        lng: worker.areaCenter.lng,
                        radiusMiles: worker.serviceRadiusMiles,
                        distanceUnit: worker.distanceUnit,
                      },
                    ]}
                  />
                ) : (
                  <Muted $size="sm">The map shows once the area is set.</Muted>
                )}
              </CardBody>
            </Card>
            <Card>
              <CardHeader title="Availability" description="Times this worker is already busy." />
              <CardBody>
                <Availability carId={worker.carId} from={state.date} zone={zone} />
              </CardBody>
            </Card>
            <WorkerReviewsCard workerCarId={worker.carId} />
          </Main>
          <Side>
            <Card>
              <CardHeader
                title={SERVICE_TYPE_LABELS[state.serviceType]}
                description={windowSummary(state)}
              />
              <CardBody>
                <Stack $gap={4}>
                  {!offersSearched ? (
                    <Muted $size="sm">{worker.displayName} does not offer this service.</Muted>
                  ) : !place ? (
                    <Stack $gap={4}>
                      <Field label="ZIP code of the job" htmlFor="worker-zip">
                        <Input
                          id="worker-zip"
                          value={zipDraft}
                          inputMode="numeric"
                          onChange={e => setZipDraft(e.target.value)}
                        />
                      </Field>
                      <Button
                        type="button"
                        theme="secondary"
                        text="See the price"
                        isDisabled={!/^\d{5}$/.test(zipDraft.trim())}
                        onClick={() =>
                          setParams(
                            serviceSearchParams({
                              ...state,
                              zip: zipDraft.trim(),
                              where: zipDraft.trim(),
                            }),
                          )
                        }
                      />
                    </Stack>
                  ) : quote.loading && !quote.data ? (
                    <LoadingBlock label="Pricing" />
                  ) : quote.error ? (
                    <Alert tone="warning" title="Cannot book this">
                      {stayErrorMessage(quote.error)}
                    </Alert>
                  ) : quote.data ? (
                    <QuoteBreakdown
                      quote={quote.data.workerQuote}
                      handoverCapHours={
                        state.serviceType === ServiceType.VehicleHandover
                          ? state.handoverHours
                          : null
                      }
                    />
                  ) : null}
                  {!worker.provider.insuranceVerified && <Muted $size="sm">{UNINSURED_NOTE}</Muted>}
                  <InlineRow>
                    <Button
                      type="button"
                      text="Book"
                      size="lg"
                      isDisabled={!offersSearched || Boolean(quote.error)}
                      onClick={() => navigate(bookPath(worker.carId, state))}
                      dataTestId="worker-detail-book"
                    />
                    <Button
                      type="button"
                      theme="secondary"
                      text="Contact worker"
                      size="lg"
                      onClick={contactWorker}
                      dataTestId="worker-detail-contact"
                    />
                  </InlineRow>
                  <Muted $size="sm">
                    {worker.instantBook
                      ? 'Instant book: the job is confirmed when you pay.'
                      : 'The worker confirms each request. You are not charged if they do not.'}
                  </Muted>
                </Stack>
              </CardBody>
            </Card>
          </Side>
        </Columns>
      </Stack>
      {contactOpen && (
        <ContactWorkerModal
          workerCarId={worker.carId}
          workerName={worker.displayName}
          onClose={() => setContactOpen(false)}
        />
      )}
    </PageContainer>
  );
}
